/**
 * Presentation helpers. Every amount on screen goes through one currency
 * formatter, so ₹ formatting can never drift page to page.
 */

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const INR_EXACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function currency(value, { exact = false } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return exact ? INR_EXACT.format(n) : INR.format(n);
}

/** Indian short scale — ₹2.5 Cr / ₹7.5 L — for dashboard tiles. */
export function currencyShort(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)} K`;
  return INR.format(n);
}

export function number(value) {
  const n = Number(value);
  return Number.isFinite(n) ? new Intl.NumberFormat('en-IN').format(n) : '—';
}

/** Quantity: 70 rather than 70.00, but 2.5 stays 2.5. */
export function quantity(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const text = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
  return unit ? `${text} ${unit}` : text;
}

export function date(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** A value for <input type="date"> — never the ISO timestamp. */
export function dateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** snake_case → Title Case, for every status/enum we render. */
export function label(value) {
  if (value == null || value === '') return '—';
  return String(value)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Pull a readable message out of an axios/KORM error. */
export function errorMessage(ex, fallback = 'Something went wrong') {
  return (
    (ex && ex.response && ex.response.data && ex.response.data.error) ||
    (ex && ex.message) ||
    fallback
  );
}

/**
 * KORM's result shape varies by action: `create` resolves to an array of rows,
 * `list` to `{data}`, `show` to the row itself. One place to untangle it.
 */
const ENVELOPE_KEYS = new Set([
  'success', 'message', 'error', 'code', 'totalCount', 'pagination',
  'cookie', 'dryRun', 'sql', 'bindings', 'statements', 'other_responses', 'policy',
]);

export function rowsOf(res) {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.data)) return res.data;
  if (res.data && typeof res.data === 'object') return [res.data];
  if (res.data !== undefined) return []; // a scalar payload (count/sum) holds no rows
  // `show` resolves to the bare row — anything that isn't purely an envelope.
  return Object.keys(res).some((k) => !ENVELOPE_KEYS.has(k)) ? [res] : [];
}

export function firstRow(res) {
  return rowsOf(res)[0] || null;
}
